import { ImagePaths } from '../../lib/imageUrls.js';

export const microsoftActiveDirectoryAdministration = {

    id: 6,
    slug: 'microsoft-active-directory-administration',
    ogImage: '/og/courses/microsoft-active-directory-administration.jpg',
    courseCode: 'AD-ADMIN-001',
    title: 'Advanced Active Directory Administration',
    subtitle: 'Master advanced Microsoft Active Directory administration techniques',
    valueProposition: 'Develop expert-level Active Directory administration skills to manage complex enterprise identity and access management infrastructure across Australian organisations.',
    duration: '32 hours',
    students: 'New',
    level: 'Intermediate',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_2,
    shortDescription: 'AD-ADMIN-001 — advanced Active Directory administration covering multi-forest design, Group Policy and AD CS for experienced AD administrators.',
    price: {
      aud: 1200,
      currency: 'AUD',
      earlyBird: 999
    },
    stripeConfig: {
    priceId: 'price_1SdKS9Ku1nyc322UkAh09ORT',
    productId: 'prod_TaVTNY8AtuBetJ'
  },
    
    snapshot: {
      whoItsFor: [
        'Active Directory administrators advancing their expertise',
        'IT professionals responsible for enterprise identity management',
        'Systems engineers designing AD infrastructure',
        'Security professionals implementing AD hardening'
      ],
      accreditation: 'Preparation for Microsoft Administrator and Security Engineer certifications'
    },
    
    logistics: {
      classSize: 'Maximum 15 participants for hands-on lab access',
      requirements: [
        'Foundational Active Directory knowledge (or completion of Fundamentals course)',
        'Experience with Active Directory administration',
        'Laptop with remote access capabilities',
        'Understanding of Group Policy basics'
      ]
    },
    
    overview: 'Advance your Active Directory administration skills with this comprehensive 32-hour course covering advanced enterprise scenarios. Learn to design and implement complex AD environments, manage multi-forest deployments, implement advanced security controls, optimise performance, and troubleshoot sophisticated issues. This course combines theoretical knowledge with extensive hands-on labs in realistic enterprise environments.',
    
    outcomes: [
      'Design and implement advanced Active Directory architectures for enterprise organisations',
      'Manage multi-forest and multi-domain environments effectively',
      'Implement advanced Group Policy strategies and security controls',
      'Configure and manage Active Directory Certificate Services (AD CS)',
      'Implement and troubleshoot Active Directory Federation Services (AD FS)',
      'Optimise Active Directory performance and replication',
      'Implement Active Directory security hardening aligned to industry frameworks',
      'Manage complex AD scenarios and troubleshoot enterprise-level issues'
    ],
    
    audience: [
      'Active Directory administrators',
      'Enterprise IT professionals',
      'Systems engineers and architects',
      'Security professionals managing identity infrastructure',
      'IT consultants supporting AD implementations'
    ],
    
    prerequisites: [
      'Understanding of Active Directory fundamentals',
      'Minimum 1-2 years of AD administration experience',
      'Knowledge of networking and security concepts',
      'Familiarity with Windows Server environments'
    ],
    
    modules: [
      {
        title: 'Advanced AD Architecture and Design',
        duration: '6 hours',
        topics: [
          'Multi-forest and multi-domain design strategies',
          'Trust relationships and cross-forest authentication',
          'Site topology and replication optimisation',
          'Azure AD integration and hybrid deployments',
          'Designing for scalability and resilience'
        ]
      },
      {
        title: 'Advanced Group Policy Management',
        duration: '6 hours',
        topics: [
          'Complex Group Policy object design and implementation',
          'Group Policy inheritance and precedence',
          'Group Policy security filtering and scope',
          'Group Policy preferences and advanced configurations',
          'Troubleshooting Group Policy issues'
        ]
      },
      {
        title: 'Certificate Services and Federation',
        duration: '6 hours',
        topics: [
          'Active Directory Certificate Services (AD CS) deployment',
          'Certificate enrolment and lifecycle management',
          'Active Directory Federation Services (AD FS) architecture',
          'Claims-based authentication and federation',
          'Implementing secure external access'
        ]
      },
      {
        title: 'Security Hardening and Compliance',
        duration: '6 hours',
        topics: [
          'Active Directory security hardening strategies',
          'Privileged access management (PAM) implementation',
          'Password policies and authentication mechanisms',
          'Monitoring and auditing Active Directory',
          'Compliance with security frameworks (ISO 27001, Essential Eight)'
        ]
      },
      {
        title: 'Performance Tuning and Troubleshooting',
        duration: '4 hours',
        topics: [
          'Active Directory performance optimisation',
          'Monitoring and diagnostics tools',
          'Troubleshooting replication and connectivity issues',
          'Resolving authentication and permission problems',
          'Enterprise support strategies'
        ]
      },
      {
        title: 'Azure AD Integration and Hybrid Identity',
        duration: '4 hours',
        topics: [
          'Azure Active Directory architecture and integration',
          'Hybrid identity implementation',
          'Identity synchronisation and cloud-only scenarios',
          'Conditional Access and Modern Authentication',
          'Managing hybrid infrastructure'
        ]
      }
    ],
    
    format: {
      type: 'Instructor-led online with extensive hands-on labs',
      includes: [
        '32 hours of expert instruction',
        'Virtual lab environments with complex AD scenarios',
        'Advanced troubleshooting simulations',
        'Real-world case studies and best practices',
        'Enterprise configuration templates',
        'Certificate of completion',
        '12 months access to course materials and labs'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Four-day intensive format (8 hours per day) with optional advanced lab access'
    },
    
    faqs: [
      {
        question: 'Do I need prior Active Directory experience?',
        answer: 'Yes, this course assumes foundational AD knowledge. You should have 1-2 years of AD administration experience or have completed our Fundamentals course before enrolling.'
      },
      {
        question: 'Will this course cover Azure AD integration?',
        answer: 'Yes, we cover Azure AD integration including hybrid deployments, synchronisation, and cloud-only scenarios. This prepares you for hybrid identity management in modern organisations.'
      },
      {
        question: 'Are hands-on labs included?',
        answer: 'Absolutely! The course includes extensive hands-on labs in realistic enterprise environments. You\'ll gain practical experience with complex scenarios and troubleshooting situations.'
      },
      {
        question: 'Will this help me with Microsoft certifications?',
        answer: 'Yes, this course aligns with Microsoft Administrator (AZ-104) and Security Engineer (AZ-500) certification paths. The content covers topics relevant to these certification exams.'
      },
      {
        question: 'Can I apply this knowledge immediately in my environment?',
        answer: 'Definitely! The course covers practical enterprise scenarios and includes configuration templates you can adapt for your organisation.'
      },
      {
        question: 'Is this suitable for those managing hybrid AD/Azure AD?',
        answer: 'Yes, we specifically cover hybrid scenarios including directory synchronisation, Azure AD Connect configuration, and managing both on-premises and cloud identities.'
      },
      {
        question: 'What support is available after the course?',
        answer: 'You\'ll have 12 months access to course materials and lab environments. We also offer optional post-course mentoring for specific implementation challenges.'
      },
      {
        question: 'Can this course be delivered in-house?',
        answer: 'Yes, we offer customised in-house training tailored to your organisation\'s specific Active Directory architecture and challenges. Contact us for custom pricing and scheduling.'
      }
    ]
};
